import { useEffect, useState } from "react"
import { useNavigate, useParams } from "react-router-dom"
import { api } from "../../../env"
import { routeEnrollment } from "../../../utils/enrollment"
import { AGREEMENT } from "../../../assets/agreemnet"
import { LoadingSpinner } from "../../../components/loading"
import { MatchingState } from "../../../utils/enums"

export default function Matching() {

    const { id } = useParams()
    const navigate = useNavigate()
    const [enrollment, setEnrollment] = useState(undefined)
    const [state, setState] = useState(MatchingState.PROPOSED)
    
    const fetchEnrollment = (autoRoute = false) => {
        if (id) api.get(`enrollments/${id}`)
            .then(res => {
                setEnrollment(res.data)
                if (autoRoute && res.data?.status === MatchingState.CONFIRMED)
                    navigate(routeEnrollment({ status: res.data.status, enrollmentId: id }))
            })
            .catch(err => {})
    }

    useEffect(() => fetchEnrollment(true), [id])

    useEffect(() => {
        if (enrollment?.status) setState(enrollment.status)
    }, [enrollment])

    const getProposal = () => {
        if (!enrollment?.matchProposal) return undefined
        let type = enrollment.type
        let matchType = type == 'Mentee' ? 'Mentor' : 'Mentee'
        let match = matchType == 'Mentee' ? enrollment.matchProposal.menteeDetails : enrollment.matchProposal.mentorDetails
        let accepted = enrollment.matchProposal.response == 'Accepted'
        return {
            match,
            matchType,
            accepted
        }
    }

    const handleAccept = () => {
        api.put(`enrollments/${id}/accept-match`)
            .then(_ => fetchEnrollment())
            .catch(err => setState(MatchingState.WAITING))
    }

    const handleReject = () => {
        api.put(`enrollments/${id}/reject-match`)
            .then(_ => fetchEnrollment())
            .catch(err => setState(MatchingState.WAITING))
    }

    const handleBegin = () => {
        navigate(routeEnrollment({ status: enrollment?.status, enrollmentId: id }))
    }

    return(<>
        {state != MatchingState.AGREEMENT && <div className="flex center justify-center h-100 standard-padding">
            {state == MatchingState.WAITING && <Waiting/>}
            {state == MatchingState.PROPOSED && <Proposed proposal={getProposal()} onReject={handleReject} onAccept={() => setState(MatchingState.AGREEMENT)}/>}
            {state == MatchingState.CONFIRMED && <Matched onBegin={handleBegin}/>}
            <div className="m-dot-1"></div>
            <div className="m-dot-2"></div>
        </div>}
        {state == MatchingState.AGREEMENT && <Agreement onReject={handleReject} onAccept={handleAccept}/>}
    </>)
}

function Waiting() {
    return(<div className="showing flex columns center upper-layer">
        <p className="section-title v-spacer-xxs text-center">Waiting for a <span className="primary medium">Match</span></p>
        <p className="card-body text-center">Please stand by until we find you a good match!</p>
    </div>)
}

function Proposed({proposal, onReject, onAccept}) {
    return(<div className="showing flex columns center upper-layer">
        <p className="section-title v-spacer-xl text-center">{!proposal?.accepted ? 'We have found you a' : 'You have accepted your '} <span className="primary medium">Match</span></p>
        {!proposal && <div className="dashed-card standard-padding v-spacer-xl" style={{width: 'clamp(100px, 85%, 500px)'}}>
            <LoadingSpinner label={'Loading your match ...'}/>
        </div>}
        {proposal && <>
            <div className="flex center gap-m v-spacer-xl" style={{maxWidth: '500px'}}>
                <div className="dashed-card standard-padding-s" style={{borderRadius: '100%', border: '3px solid rgb(var(--primary-dark))'}}><span className="material-symbols-outlined scale-xxl">{proposal.matchType == 'Mentor' ? 'supervisor_account' : 'person'}</span></div>
                <div className="v-spacer-xs" style={{width: 'clamp(100px, 85%, 500px)'}}>
                    <div className="flex gap-xxs center v-spacer-xxs">
                        <p className="card-title">{proposal.match.firstName} {proposal.match.lastName}</p>
                        <p className="chip primary-light-chip card-label scale-xs">{proposal.matchType}</p></div>
                    <p className="card-label">{proposal.match.organization}</p>
                </div>
            </div>
            {proposal.accepted && <p className="card-body medium primary">Please wait for your matches response!</p>}
        </>}
        {!proposal?.accepted && <div className="flex gap-xs center">
            <button className="outline" onClick={onReject}>Reject</button>
            <button className="solid" onClick={onAccept}>Accept</button>
        </div>}
    </div>)
}

function Agreement({onReject, onAccept}) {

    const [agree, setAgree] = useState(false)

    return(<div className="bg-target w-100" style={{marginTop: '-98px'}}>
        <div className="showing standard-padding-sm columns flex center justify-end" style={{background: "rgb(var(--primary-dark))", color: "rgb(var(--on-primary-dark))"}}>
            <p className="section-title" style={{paddingTop: '88px'}}>Mentorship Agreement</p>
        </div>
        <div className="standard-padding page-xl-centered">
            {AGREEMENT.map(item => <div className="two-columns gap-s v-spacer-xxl" style={{gridTemplateColumns: '250px 1fr'}}>
                <p className="card-title dashed-card padding-sl" style={{background: 'rgba(var(--primary-dark), 10%)'}}>{item.title}</p>
                <div className="standard-padding-xs flex columns justify-center">
                    {item.content.map(line => <AgreementContentLine line={line}/>)}
                </div>
            </div>)}
            <div className="dashed-card columns standard-padding-sm gap-l">
                <div className="flex center interactable gap-s" onClick={() => setAgree(!agree)}>
                    <input type="checkbox" checked={agree} style={{width: '50px'}}/>
                    <p className="card-body medium" style={{textAlign: 'start !important'}}>By checking this box, I confirm that I have read and accept the terms of the Mentorship Agreement.</p>
                </div>
                <div className="two-columns wrap center gap-xs" style={{width: 'clamp(50px, 100%, 500px)', gridTemplateColumns: '120px 1fr'}}>
                    <button className="outline large with-icon leading" onClick={onReject}><span className="material-symbols-outlined scale-s h-spacer-xs">undo</span> Reject</button>
                    <button className={`solid-accent large with-icon leading ${!agree ? 'disabled' : ''}`} disabled={!agree} onClick={onAccept}><span className="material-symbols-outlined scale-s h-spacer-xs">check</span> Sign Agreement & Accept the Match</button>
                </div>
            </div>
        </div>
    </div>)
}

function AgreementContentLine({line}) {
    const [bolded, setBolded] = useState(undefined)
    const [regular, setRegular] = useState("")

    useEffect(() => {
        if (!line) return
        let segments = line.split(':')
        if (segments.length > 1) {
            setBolded(segments[0])
            segments.splice(0, 1)
        }
        setRegular(segments.join(':'))
    }, [line])

    return(<>
        <p className="card-body regular">{bolded && <span className="bold">{bolded}:</span>} {regular}</p>
    </>)
}

function Matched({onBegin}) {
    return(<div className="showing flex columns center upper-layer">
        <p className="section-title v-spacer-m text-center">You have been successfully <span className="primary medium">Matched</span>!</p>
        <button className="solid large with-icon trailing" onClick={onBegin}>
            Begin Your Journey
            <span className="material-symbols-outlined scale-s">keyboard_double_arrow_right</span>
        </button>
    </div>)
}